"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { NAV_ITEMS } from "@/constants/nav-items";
import {
  ShoppingCart,
  Wallet,
  BarChart3,
  Warehouse,
  Package,
  Users,
  FileText,
  Settings,
  Home,
  ArrowLeftRight,
  Menu,
  X
} from "lucide-react";

function normalizeHref(href: string) {
  if (!href) return "/";
  const trimmed = href.trim();
  if (trimmed === "/") return "/";
  return trimmed.replace(/\/+$/, "");
}

function makeSafeId(input: string) {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getNavIcon(routeKey: string, isActive: boolean) {
  const key = normalizeHref(routeKey);

  const iconClass = `w-6 h-6 transition-all duration-300 ${
    isActive
      ? "text-blue-600 dark:text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]"
      : "text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 dark:group-hover:drop-shadow-[0_0_6px_rgba(59,130,246,0.4)]"
  }`;

  switch (key) {
    case "/venta":
    case "/sales":
    case "/point-of-sale":
      return <ShoppingCart className={iconClass} />;
    case "/caja":
    case "/cash":
      return <Wallet className={iconClass} />;
    case "/reportes":
    case "/reports":
      return <BarChart3 className={iconClass} />;
    case "/almacen":
    case "/almacenes":
    case "/warehouse":
      return <Warehouse className={iconClass} />;
    case "/productos":
    case "/products":
      return <Package className={iconClass} />;
    case "/sustitutos":
    case "/substitutes":
      return <ArrowLeftRight className={iconClass} />;
    case "/proveedores":
    case "/suppliers":
      return <Users className={iconClass} />;
    case "/cotizaciones":
    case "/quotes":
      return <FileText className={iconClass} />;
    case "/config":
    case "/configuracion":
    case "/configuration":
      return <Settings className={iconClass} />;
    default:
      return <Home className={iconClass} />;
  }
}

type NavItem = {
  href: string;
  label: string;
  icon?: ReactNode;
  [key: string]: any;
};

/**
 * Returns ALL nav items — no feature filtering.
 * Plan validation is handled by PlanGate in AppShell,
 * which blocks page CONTENT instead of hiding nav items.
 */
function useAllowedNavItems() {
  const allowedNavItems = useMemo(() => {
    return (NAV_ITEMS as unknown as NavItem[]).filter(
      (i) => i?.href && i.href !== "/"
    );
  }, []);

  return { allowedNavItems };
}

function MobileBottomBar({ items }: { items: NavItem[] }) {
  const pathnameRaw = usePathname();
  const pathname = useMemo(() => normalizeHref(pathnameRaw || "/"), [pathnameRaw]);

  const isActive = (href: string) => {
    const target = normalizeHref(href);
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(`${target}/`);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200/60 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/70 md:hidden">
      <div className="mx-auto flex max-w-md items-stretch justify-between px-2 py-2">
        {items.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={[
                "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-[11px] font-semibold",
                active
                  ? "bg-indigo-600 text-white shadow shadow-indigo-600/20"
                  : "text-gray-700 hover:bg-gray-100/70 dark:text-slate-200 dark:hover:bg-slate-800/60",
              ].join(" ")}
            >
              <span className="text-[18px] leading-none">{item.icon ?? "•"}</span>
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function MobileSideDrawer({ items }: { items: NavItem[] }) {
  const pathnameRaw = usePathname();
  const pathname = useMemo(() => normalizeHref(pathnameRaw || "/"), [pathnameRaw]);
  const [open, setOpen] = useState(false);

  const isActive = (href: string) => {
    const target = normalizeHref(href);
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(`${target}/`);
  };

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="fixed left-3 top-2.5 z-[70] inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/40 bg-white/80 text-slate-800 shadow-lg shadow-slate-900/10 backdrop-blur-xl transition active:scale-95 dark:border-slate-700/60 dark:bg-slate-900/80 dark:text-white md:hidden"
        aria-label={open ? "Cerrar menu" : "Abrir menu"}
        aria-expanded={open}
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[65] bg-slate-950/45 backdrop-blur-sm md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-[75] flex w-[min(20rem,86vw)] flex-col overflow-hidden border-r border-slate-200/70 bg-white/95 shadow-2xl shadow-slate-950/25 backdrop-blur-2xl transition-transform duration-300 md:hidden dark:border-slate-800/70 dark:bg-slate-950/95",
          open ? "translate-x-0" : "-translate-x-full pointer-events-none",
        ].join(" ")}
        aria-hidden={!open}
      >
        <div className="flex h-16 items-center justify-between border-b border-slate-200/70 px-4 dark:border-slate-800/70">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 shadow-lg">
              <span className="text-lg font-extrabold text-white">P</span>
            </div>
            <div className="min-w-0">
              <p className="text-base font-extrabold text-slate-900 dark:text-white">POS MOD</p>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Menu principal</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="grid h-10 w-10 place-items-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 active:scale-95 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
            aria-label="Cerrar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {items.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={[
                  "group flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition",
                  active
                    ? "border border-blue-200/70 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-700/50 dark:bg-blue-950/40 dark:text-blue-200"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800/70 dark:hover:text-white",
                ].join(" ")}
              >
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100/80 dark:bg-slate-900">
                  {getNavIcon(item.href, active)}
                </span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}

export default function ModernSidebar() {
  const pathnameRaw = usePathname();
  const pathname = useMemo(() => normalizeHref(pathnameRaw || "/"), [pathnameRaw]);

  const { allowedNavItems } = useAllowedNavItems();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Make the sidebar aware of a top navbar/header.
  // Prefer an element with [data-app-navbar] so you can control it precisely:
  //   <header data-app-navbar ... />
  // Fallback: first <header> found.
  const [topOffsetPx, setTopOffsetPx] = useState(0);

  const isActive = (href: string) => {
    const target = normalizeHref(href);
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(`${target}/`);
  };

  useEffect(() => {
    try {
      const savedCollapsed = localStorage.getItem("posmod.sidebar.collapsed");
      const savedFirstLoad = localStorage.getItem("posmod.sidebar.firstLoad");
      if (savedCollapsed) setIsCollapsed(savedCollapsed === "1");
      setIsFirstLoad(savedFirstLoad === null);
    } catch {}
  }, []);

  useEffect(() => {
    const getHeaderEl = () => {
      if (typeof document === "undefined") return null;
      return (
        (document.querySelector("[data-app-navbar]") as HTMLElement | null) ||
        (document.querySelector("header") as HTMLElement | null)
      );
    };

    const headerEl = getHeaderEl();
    const fallback = 64;
    if (!headerEl) {
      setTopOffsetPx(fallback);
      return;
    }

    const update = () => {
      const h = Math.round(headerEl.getBoundingClientRect().height || 0);
      setTopOffsetPx(h > 0 ? h : fallback);
    };

    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(headerEl);
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("posmod.sidebar.collapsed", isCollapsed ? "1" : "0");
    } catch {}
  }, [isCollapsed]);

  useEffect(() => {
    if (isFirstLoad && !isCollapsed) {
      const timer = setTimeout(() => {
        setIsCollapsed(true);
        try {
          localStorage.setItem("posmod.sidebar.firstLoad", "false");
          localStorage.setItem("posmod.sidebar.collapsed", "1");
        } catch {}
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isFirstLoad, isCollapsed]);

  return (
    <>
      <aside
        style={{ top: topOffsetPx, height: `calc(100vh - ${topOffsetPx}px)` }}
        className={`hidden lg:flex [@media(pointer:coarse)]:hidden sticky border-r border-gray-200/20 dark:border-gray-700/40
        bg-gradient-to-b from-white/95 via-blue-50/40 to-purple-50/40
        dark:from-slate-900 dark:via-slate-800 dark:to-slate-800
        backdrop-blur-xl shadow-2xl shadow-blue-500/15
        flex-col transition-all duration-700 ease-in-out
        ${isCollapsed ? "w-20" : "w-72"} overflow-x-hidden overflow-y-auto`}
      >
        <div
          className={`h-14 flex items-center border-b border-gray-200/30 dark:border-gray-700/40
          transition-all duration-500 ${isCollapsed ? "justify-center" : "justify-between px-5"}`}
        >
          {!isCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 dark:from-indigo-400 dark:to-fuchsia-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-extrabold text-lg">P</span>
              </div>
              <span className="text-2xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-indigo-400 dark:to-pink-500 bg-clip-text text-transparent">
                POS MOD
              </span>
            </div>
          )}

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-2 rounded-full transition-all duration-500 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 ${
              isCollapsed ? "mx-auto" : "ml-3"
            }`}
            aria-label="Toggle Sidebar"
            title="Toggle Sidebar"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform duration-700 ${isCollapsed ? "rotate-180" : ""}`}
            >
              <defs>
                <linearGradient id="toggleGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
              <path
                d={isCollapsed ? "M8 18L16 12L8 6" : "M16 18L8 12L16 6"}
                stroke="url(#toggleGrad)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <nav className={`flex-1 pb-8 space-y-2 mt-6 ${isCollapsed ? "px-3" : "px-4"}`}>
          {allowedNavItems.map((item) => {
            const active = isActive(item.href);
            const hovered = hoveredItem === item.href;

            const icon = getNavIcon(item.href, active);

            return (
              <Link
                key={item.href}
                href={item.href}
                onMouseEnter={() => setHoveredItem(item.href)}
                onMouseLeave={() => setHoveredItem(null)}
                onFocus={() => setHoveredItem(item.href)}
                onBlur={() => setHoveredItem(null)}
                aria-label={item.label}
                title={item.label}
                aria-current={active ? "page" : undefined}
                className={`group flex items-center rounded-2xl py-3.5 font-medium transition-all duration-300 relative
                  ${isCollapsed ? "justify-center px-0" : "justify-start gap-3.5 px-4"}
                  ${
                    active
                      ? "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/20 dark:shadow-blue-500/30 border border-blue-200/60 dark:border-blue-700/50"
                      : "text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800/70 dark:hover:shadow-[0_0_15px_rgba(59,130,246,0.15)]"
                  }
                  ${hovered && !active ? "transform scale-[1.02]" : ""}`}
              >
                <div className="relative z-10 flex-shrink-0">
                  {icon}
                </div>

                {!isCollapsed && (
                  <span className="relative z-10 text-sm font-semibold overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                )}

                {isCollapsed && (
                  <>
                    <span className="sr-only">{item.label}</span>
                    <div
                      className="pointer-events-none absolute left-full ml-3 px-3 py-2 bg-gray-900/95 dark:bg-gray-800/95 text-white text-sm font-medium rounded-xl opacity-0
                      group-hover:opacity-100 group-focus-visible:opacity-100
                      transition-all duration-200 shadow-xl dark:shadow-[0_0_20px_rgba(59,130,246,0.4)] z-50
                      group-hover:translate-x-1 group-focus-visible:translate-x-1 whitespace-nowrap
                      border border-gray-700/50 dark:border-blue-500/30"
                    >
                      {item.label}
                    </div>
                  </>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      <MobileSideDrawer items={allowedNavItems} />
    </>
  );
}
